import React, { useState, useEffect, useRef } from 'react';
import AuthAwareCTAButtons from './AuthAwareCTAButtons';
import useBackgroundImagePreload from '../hooks/useBackgroundImagePreload';

const Hero = () => {
  const [counts, setCounts] = useState({ colleges: 0, tools: 0, students: 0 });
  const [hasAnimated, setHasAnimated] = useState(false);
  const statsRef = useRef(null);
  const backgroundImageSrc = '/landing-bg.jpeg';
  const isBackgroundLoaded = useBackgroundImagePreload(backgroundImageSrc);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !hasAnimated) {
          setHasAnimated(true);
          animateCounters();
        }
      },
      { threshold: 0.5 }
    );

    if (statsRef.current) {
      observer.observe(statsRef.current);
    }

    return () => observer.disconnect();
  }, [hasAnimated]);

  const animateCounters = () => {
    const duration = 2000;
    const targets = { colleges: 3000, tools: 5, students: 1000 };
    const steps = 60;
    const stepDuration = duration / steps;

    let currentStep = 0;

    const interval = setInterval(() => {
      currentStep++;
      const progress = currentStep / steps;

      setCounts({
        colleges: Math.floor(targets.colleges * progress),
        tools: Math.floor(targets.tools * progress),
        students: Math.floor(targets.students * progress)
      });

      if (currentStep >= steps) {
        setCounts(targets);
        clearInterval(interval);
      }
    }, stepDuration);
  };

  return (
    <section className="relative overflow-hidden bg-[linear-gradient(135deg,#f8f4fb,#fff8f3)] py-12 sm:py-16 md:py-20">
      <div
        className={`absolute inset-0 bg-cover bg-center bg-no-repeat transition-opacity duration-300 ease-out ${
          isBackgroundLoaded ? 'opacity-100' : 'opacity-0'
        }`}
        style={{ backgroundImage: `url('${backgroundImageSrc}')` }}
      />
      <div className="absolute inset-0 bg-[linear-gradient(135deg,rgba(255,255,255,0.18),rgba(255,255,255,0.10)),radial-gradient(circle_at_top_left,_rgba(154,81,160,0.06),_transparent_28%),radial-gradient(circle_at_bottom_right,_rgba(243,111,33,0.06),_transparent_32%)]" />
      <div className="container relative mx-auto px-4 sm:px-6 text-center">
        
        {/* Main Heading */}
        <h1 className="text-heading-hero mb-6 sm:mb-8 md:mb-10 text-[#1f1f23] drop-shadow-[0_2px_12px_rgba(255,255,255,0.45)]">
          Find Your Perfect
          <span className="bg-gradient-to-r from-[#8f3fa3] to-[#ef6b1f] bg-clip-text text-transparent block pb-2 sm:pb-4 drop-shadow-none">
            College Match
          </span>
        </h1>
        
        {/* Description */}
        <p className="text-body-large mb-6 sm:mb-8 max-w-3xl mx-auto px-2 sm:px-0 text-[#2f3441] drop-shadow-[0_1px_8px_rgba(255,255,255,0.35)]">
          <span className="block">Explore colleges across India, compare institutions,</span>
          <span className="block">and make informed decisions with coding tools, tutorials,</span>
          <span className="block">and detailed college insights in one place.</span>
        </p>

        {/* CTA Buttons - All in one row */}
        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-center mb-8 sm:mb-12 px-4 sm:px-0">
          <AuthAwareCTAButtons />
          <a
            href="/admin_login"
            className="btn-admin w-full sm:w-auto min-h-[48px]"
          >
            Admin Login
          </a>
        </div>

        {/* Statistics Section */}
        <div ref={statsRef} className="grid grid-cols-3 gap-3 sm:gap-8 max-w-4xl mx-auto">
          <div className="text-center">
            <div className="text-stat-number text-[#9a51a0]">{counts.colleges}+</div>
            <div className="text-stat-label">Top Colleges</div>
          </div>
          <div className="text-center">
            <div className="text-stat-number text-[#f36f21]">{counts.tools}</div>
            <div className="text-stat-label">Smart Tools</div>
          </div>
          <div className="text-center">
            <div className="text-stat-number text-[#9a51a0]">{counts.students}+</div>
            <div className="text-stat-label">Students Helped</div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
